import React, { useCallback, useMemo } from 'react';
import { ConfigurationSelect, Section } from '@ydistri/ds';
import { SelectProps } from 'antd';
import { RegionFlowType } from '@ydistri/api-sdk';
import {
  UpdateCalculationConfigurationPayload,
  useGetCalculationConfigurationQuery,
  useUpdateCalculationConfigurationMutation,
} from '../../../../apis/apiGlobalConfiguration';
import {
  ConfigurationFieldStatus,
  rtkQueryPatchStatusToConfigurationFieldStatus,
} from '@ydistri/utils';
import useIsConfigurationEditable from '../../../../hooks/useIsConfigurationEditable';
import { useTemplateOrCalculationWithValue } from '../../../../hooks/useTemplateOrCalculationWithValue';
import { skipToken } from '@reduxjs/toolkit/query';

const RegionFlowTypeSection: React.FC = () => {
  const templateOrCalculation = useTemplateOrCalculationWithValue();
  const isConfigurationEditable = useIsConfigurationEditable();

  const { data: calculationConfiguration, isFetching } = useGetCalculationConfigurationQuery(
    templateOrCalculation.value,
  );
  const [updateConfiguration, updateConfigurationStatus] =
    useUpdateCalculationConfigurationMutation();

  const options: SelectProps<RegionFlowType>['options'] = useMemo(() => {
    return [
      {
        value: RegionFlowType.AllEnabled,
        label: 'Redistribute among all enabled regions',
      },
      {
        value: RegionFlowType.WithinRegion,
        label: 'Redistribute within each region individually',
      },
      {
        value: RegionFlowType.CustomRedistribution,
        label: 'Custom setup of redistribution flow',
      },
    ];
  }, []);

  const onChangeHandler = useCallback(
    (value: RegionFlowType) => {
      if (templateOrCalculation.hasValidValue && templateOrCalculation.value !== skipToken) {
        const payload: UpdateCalculationConfigurationPayload = {
          templateOrCalculation: templateOrCalculation.value,
          request: {
            regionFlowTypeId: value,
          },
        };

        updateConfiguration(payload);
      }
    },
    [templateOrCalculation, updateConfiguration],
  );

  const status = isFetching
    ? ConfigurationFieldStatus.LOADING
    : rtkQueryPatchStatusToConfigurationFieldStatus(updateConfigurationStatus);

  const headerActions = useMemo(
    () => (
      <ConfigurationSelect
        disabled={!(isConfigurationEditable && templateOrCalculation.hasValidValue)}
        value={calculationConfiguration?.regionFlowTypeId}
        options={options}
        onChange={onChangeHandler}
        status={status}
        showArrow={true}
        width="400px"
        size="large"
      />
    ),
    [
      calculationConfiguration?.regionFlowTypeId,
      isConfigurationEditable,
      onChangeHandler,
      options,
      status,
      templateOrCalculation.hasValidValue,
    ],
  );

  return (
    <Section
      header="Region flow type"
      headerActions={headerActions}
      headerActionsJustifyContent="flex-start"
      $ratio={0}
    />
  );
};

export default RegionFlowTypeSection;
